import type { RepairRunRequest } from "contracts/api/crawl/requests";
import {
  CrawlAbortResponseSchema,
  CreateCrawlRunResponseSchema,
  RepairRunResponseSchema,
} from "contracts/api/crawl/responses";
import {
  CurrentRunResponseSchema,
  RunDetailResponseSchema,
  RunListResponseSchema,
  type RunPageCursor,
} from "contracts/api/crawl/run-read.responses";
import { PageDetailResponseSchema } from "contracts/api/dashboard/page-detail.responses";
import {
  ContentFitnessDimensionResponseSchema,
  OverviewResponseSchema,
} from "contracts/api/dashboard/responses";
import type { IssueWriteRequest } from "contracts/api/issue/requests";
import { IssueSchema } from "contracts/api/issue/responses";
import { XLSX_CONTENT_TYPE, XLSX_EXTENSION } from "contracts/api/media-types";
import type {
  MetricCatalogUpdateRequest,
  MetricReportUpdateRequest,
} from "contracts/api/metric/requests";
import {
  MetricCatalogSchema,
  MetricDetailSchema,
} from "contracts/api/metric/responses";
import type { UpdateScanScheduleRequest } from "contracts/api/website/requests";
import {
  ScanScheduleSchema,
  WebsiteHealthSummaryListSchema,
} from "contracts/api/website/responses";
import {
  WebsiteDeleteResponseSchema,
  WebsiteSettingsResponseSchema,
  type WebsiteSettingsUpdateRequest,
} from "contracts/api/website/settings";
import { request } from "./api-client";
import { requestDownload } from "./api-download";

export const dashboardApi = {
  screenshotPath: (websiteId: string, pageId: string, screenshotId: string) =>
    `/api/websites/${websiteId}/pages/${pageId}/screenshots/${screenshotId}`,
  listWebsites: () =>
    request("GET", "/api/websites", WebsiteHealthSummaryListSchema),
  overview: (websiteId: string) =>
    request(
      "GET",
      `/api/websites/${websiteId}/overview`,
      OverviewResponseSchema,
    ),
  // The export ignores the screen's filters by design: it is the whole issue
  // list for the website, so no filter state is sent with it.
  exportIssues: (websiteId: string, signal?: AbortSignal) =>
    requestDownload(
      `/api/websites/${websiteId}/issues/export.xlsx`,
      { contentType: XLSX_CONTENT_TYPE, extension: XLSX_EXTENSION },
      signal,
    ),
  metric: (websiteId: string, metricKey: string) =>
    request(
      "GET",
      `/api/websites/${websiteId}/metrics/${metricKey}`,
      MetricDetailSchema,
    ),
  contentFitnessDimension: (websiteId: string, dimensionKey: string) =>
    request(
      "GET",
      `/api/websites/${websiteId}/content-fitness/${dimensionKey}`,
      ContentFitnessDimensionResponseSchema,
    ),
  pageDetail: (websiteId: string, pageId: string) =>
    request(
      "GET",
      `/api/websites/${websiteId}/pages/${pageId}`,
      PageDetailResponseSchema,
    ),
  setIssueStatus: (issueId: string, status: string) =>
    request("PATCH", `/api/issues/${issueId}/status`, IssueSchema, { status }),
  createIssue: (websiteId: string, input: IssueWriteRequest) =>
    request("POST", `/api/websites/${websiteId}/issues`, IssueSchema, input),
  updateIssue: (issueId: string, input: IssueWriteRequest) =>
    request("PUT", `/api/issues/${issueId}`, IssueSchema, input),
  archiveIssue: (issueId: string) =>
    request("DELETE", `/api/issues/${issueId}`, IssueSchema),
  updateMetricCatalog: (metricKey: string, input: MetricCatalogUpdateRequest) =>
    request("PUT", `/api/metrics/${metricKey}`, MetricCatalogSchema, input),
  updateMetricReport: (
    websiteId: string,
    metricKey: string,
    input: MetricReportUpdateRequest,
  ) =>
    request(
      "PUT",
      `/api/websites/${websiteId}/metrics/${metricKey}/report`,
      MetricDetailSchema,
      input,
    ),
  currentRun: (websiteId: string) =>
    request(
      "GET",
      `/api/websites/${websiteId}/runs/current`,
      CurrentRunResponseSchema,
    ),
  listRuns: (websiteId: string) =>
    request("GET", `/api/websites/${websiteId}/runs`, RunListResponseSchema),
  runDetail: (
    websiteId: string,
    runId: string,
    after: RunPageCursor | null,
  ) => {
    const query = after
      ? `?afterDepth=${after.depth}&afterUrl=${encodeURIComponent(after.url)}`
      : "";
    return request(
      "GET",
      `/api/websites/${websiteId}/runs/${runId}${query}`,
      RunDetailResponseSchema,
    );
  },
  abortRun: (websiteId: string, runId: string) =>
    request(
      "POST",
      `/api/websites/${websiteId}/runs/${runId}/abort`,
      CrawlAbortResponseSchema,
    ),
  repairRun: (websiteId: string, runId: string, input: RepairRunRequest) =>
    request(
      "POST",
      `/api/websites/${websiteId}/runs/${runId}/repair`,
      RepairRunResponseSchema,
      input,
    ),
  scan: (websiteId: string) =>
    request(
      "POST",
      `/api/websites/${websiteId}/scan`,
      CreateCrawlRunResponseSchema,
    ),
  setScanSchedule: (websiteId: string, input: UpdateScanScheduleRequest) =>
    request(
      "PUT",
      `/api/websites/${websiteId}/scan-schedule`,
      ScanScheduleSchema,
      input,
    ),
  clearScanSchedule: (websiteId: string) =>
    request(
      "DELETE",
      `/api/websites/${websiteId}/scan-schedule`,
      ScanScheduleSchema,
    ),
  websiteSettings: (websiteId: string) =>
    request(
      "GET",
      `/api/websites/${websiteId}/settings`,
      WebsiteSettingsResponseSchema,
    ),
  updateWebsiteSettings: (
    websiteId: string,
    input: WebsiteSettingsUpdateRequest,
  ) =>
    request(
      "PATCH",
      `/api/websites/${websiteId}/settings`,
      WebsiteSettingsResponseSchema,
      input,
    ),
  deleteWebsite: (websiteId: string) =>
    request(
      "DELETE",
      `/api/websites/${websiteId}`,
      WebsiteDeleteResponseSchema,
    ),
};
